"use client"

import { useState, useEffect, useRef } from "react"
import "./CartPage.css"
import { getAuth } from "firebase/auth"
import { database } from "../firebase" // Import the database from firebase.js
import { ref, get, set, child, push } from "firebase/database" // Import Realtime Database methods
import { Link, useNavigate } from "react-router-dom" // Added useNavigate
// Import the useCart hook
import { useCart } from "../Context/CartContext"

export default function CartPage() {
  // Use the cart context instead of local state for cart items
  const { cartItems, updateQuantity, removeItem, getCartTotals, createOrder } = useCart()

  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
  })
  const [savedAddresses, setSavedAddresses] = useState([])
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [saveAddress, setSaveAddress] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const animationContainer = useRef(null)
  const navigate = useNavigate() // Add navigate hook

  // Get current user
  const auth = getAuth()

  // Get cart totals
  const { subtotal, tax, shipping, total } = getCartTotals()

  useEffect(() => {
    // Initialize Lottie animation - using a simpler approach to avoid XMLHttpRequest issues
    if (animationContainer.current) {
      try {
        // Use a simple loading spinner instead of JSON animation to avoid XMLHttpRequest issues
        animationContainer.current.innerHTML = `
          <div style="display: flex; justify-content: center; align-items: center; height: 100%;">
            <div style="border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 2s linear infinite;"></div>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `
      } catch (err) {
        console.error("Error with loading animation:", err)
      }
    }
  }, [])

  useEffect(() => {
    const fetchSavedAddresses = async () => {
      if (auth.currentUser) {
        try {
          // Reference to the user's data in Realtime Database
          const dbRef = ref(database)

          // Use child references to avoid direct XMLHttpRequest issues
          try {
            // Get addresses
            const addressesSnapshot = await get(child(dbRef, `users/${auth.currentUser.uid}/addresses`))

            if (addressesSnapshot.exists()) {
              const addressesData = addressesSnapshot.val()
              // Convert to array if it's an object
              const addressesArray = Array.isArray(addressesData) ? addressesData : Object.values(addressesData)

              setSavedAddresses(addressesArray)

              // Get default address
              const defaultSnapshot = await get(child(dbRef, `users/${auth.currentUser.uid}/defaultAddress`))

              if (defaultSnapshot.exists()) {
                const defaultId = defaultSnapshot.val()
                const defaultAddress = addressesArray.find((addr) => addr.id === defaultId)

                if (defaultAddress) {
                  setSelectedAddress(defaultId)
                  setAddress(defaultAddress)
                }
              }
            } else {
              setSavedAddresses([])
            }

            setError(null)
          } catch (dbError) {
            console.error("Database error:", dbError)
            setError("Database connection error. Please try again later.")
          }
        } catch (error) {
          console.error("Error fetching addresses:", error)
          setError("Failed to load saved addresses. Please try again.")
        }
      }
      setLoading(false)
    }

    fetchSavedAddresses()
  }, [auth.currentUser])

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear selected address when user starts typing a new one
    if (selectedAddress) {
      setSelectedAddress(null)
    }
  }

  const handleAddressSelect = (addressId) => {
    const selected = savedAddresses.find((addr) => addr.id === addressId)
    if (selected) {
      setSelectedAddress(addressId)
      setAddress(selected)
    }
  }

  const handleSaveAddress = async () => {
    if (!auth.currentUser) return

    try {
      const newAddress = {
        ...address,
        id: Date.now().toString(),
      }

      // Use a different approach to avoid XMLHttpRequest issues
      const dbRef = ref(database)

      // Get current addresses first
      const addressesSnapshot = await get(child(dbRef, `users/${auth.currentUser.uid}/addresses`))

      let updatedAddresses = []

      if (addressesSnapshot.exists()) {
        const addressesData = addressesSnapshot.val()
        // Handle both array and object formats
        updatedAddresses = Array.isArray(addressesData)
          ? [...addressesData, newAddress]
          : [...Object.values(addressesData), newAddress]
      } else {
        updatedAddresses = [newAddress]
      }

      // Save as an object with push IDs to avoid array issues in Firebase
      const addressesRef = child(dbRef, `users/${auth.currentUser.uid}/addresses`)

      // Clear existing addresses and set new ones
      await set(addressesRef, null) // Clear first

      // Add each address with a unique key
      for (const addr of updatedAddresses) {
        const newRef = push(addressesRef)
        await set(newRef, addr)
      }

      // Set default address if needed
      if (saveAddress) {
        await set(child(dbRef, `users/${auth.currentUser.uid}/defaultAddress`), newAddress.id)
      }

      // Update local state
      setSavedAddresses(updatedAddresses)
      setSelectedAddress(newAddress.id)
      setError(null)
    } catch (error) {
      console.error("Error saving address:", error)
      setError("Failed to save address. Please try again.")
    }
  }

  const handleCheckout = async () => {
    // Validate address
    if (!address.street || !address.city || !address.state || !address.zipCode || !address.phone) {
      setError("Please fill in all address fields")
      return
    }

    // Save address if checkbox is checked
    if (saveAddress) {
      await handleSaveAddress()
    }

    try {
      // Create order in Firebase
      const orderNumber = await createOrder(address)

      if (orderNumber) {
        // Navigate to confirmation page
        navigate("/confirm")
      } else {
        setError("Failed to create order. Please try again.")
      }
    } catch (error) {
      console.error("Error during checkout:", error)
      setError("An error occurred during checkout. Please try again.")
    }
  }

  return (
    <div className="mince_cart_container">
      <div className="mince_cart_wrapper">
        {error && (
          <div
            className="mince_error_message"
            style={{
              color: "red",
              padding: "10px",
              margin: "10px 0",
              backgroundColor: "#ffeeee",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        <div className="mince_cart_layout">
          {/* Cart Items - Left Side */}
          <div className="mince_cart_items_container">
            <div className="mince_cart_card">
              <div className="mince_card_content">
                <h2 className="mince_section_title">Cart Items</h2>

                {cartItems.length === 0 ? (
                  <div className="mince_empty_cart">
                    <p>Your cart is empty</p>
                    <Link to="/catalog">
                      <button className="mince_primary_button">Continue Shopping</button>
                    </Link>
                  </div>
                ) : (
                  <div className="mince_cart_items_list">
                    {cartItems.map((item) => (
                      <div key={item.id} className="mince_cart_item">
                        <div className="mince_item_image">
                          <img src={item.image || "/placeholder.svg?height=100&width=100"} alt={item.name} />
                        </div>
                        <div className="mince_item_details">
                          <h3>{item.name}</h3>
                          <p className="mince_item_price">₹{item.price.toFixed(2)}</p>
                        </div>
                        <div className="mince_item_quantity">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="mince_quantity_button"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <span className="mince_sr_only">Decrease quantity</span>
                          </button>
                          <span className="mince_quantity_value">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="mince_quantity_button"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="16"></line>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                            </svg>
                            <span className="mince_sr_only">Increase quantity</span>
                          </button>
                        </div>
                        <div className="mince_item_total">
                          <p className="mince_total_price">₹{(item.price * item.quantity).toFixed(2)}</p>
                          <button onClick={() => removeItem(item.id)} className="mince_remove_button">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            <span className="mince_sr_only">Remove item</span>
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="mince_separator"></div>

                    <div className="mince_order_summary">
                      <div className="mince_summary_row">
                        <p className="mince_summary_label">Subtotal</p>
                        <p className="mince_summary_value">₹{subtotal.toFixed(2)}</p>
                      </div>
                      <div className="mince_summary_row">
                        <p className="mince_summary_label">Tax</p>
                        <p className="mince_summary_value">₹{tax.toFixed(2)}</p>
                      </div>
                      <div className="mince_summary_row">
                        <p className="mince_summary_label">Shipping</p>
                        <p className="mince_summary_value">₹{shipping.toFixed(2)}</p>
                      </div>
                      <div className="mince_separator"></div>
                      <div className="mince_summary_row mince_total">
                        <p className="mince_total_label">Total</p>
                        <p className="mince_total_value">₹{total.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checkout Form - Right Side */}
          <div className="mince_checkout_container">
            <div className="mince_cart_card">
              <div className="mince_card_content">
                <h2 className="mince_section_title">Shipping Information</h2>

                {loading ? (
                  <div className="mince_loading_container">
                    <div ref={animationContainer} className="mince_lottie_animation" />
                  </div>
                ) : (
                  <>
                    {savedAddresses.length > 0 && (
                      <div className="mince_saved_addresses">
                        <label htmlFor="saved-address" className="mince_form_label">
                          Saved Addresses
                        </label>
                        <select
                          id="saved-address"
                          className="mince_address_select"
                          value={selectedAddress || ""}
                          onChange={(e) => handleAddressSelect(e.target.value)}
                        >
                          <option value="">-- Select a saved address --</option>
                          {savedAddresses.map((addr) => (
                            <option key={addr.id} value={addr.id}>
                              {addr.street}, {addr.city}, {addr.state} {addr.zipCode}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="mince_form_container">
                      <div className="mince_form_group">
                        <label htmlFor="street" className="mince_form_label">
                          Street Address
                        </label>
                        <input
                          id="street"
                          name="street"
                          type="text"
                          placeholder="123 Main St"
                          value={address.street}
                          onChange={handleAddressChange}
                          className="mince_form_input"
                        />
                      </div>

                      <div className="mince_form_row">
                        <div className="mince_form_group">
                          <label htmlFor="city" className="mince_form_label">
                            City
                          </label>
                          <input
                            id="city"
                            name="city"
                            type="text"
                            placeholder="New York"
                            value={address.city}
                            onChange={handleAddressChange}
                            className="mince_form_input"
                          />
                        </div>
                        <div className="mince_form_group">
                          <label htmlFor="state" className="mince_form_label">
                            State
                          </label>
                          <input
                            id="state"
                            name="state"
                            type="text"
                            placeholder="NY"
                            value={address.state}
                            onChange={handleAddressChange}
                            className="mince_form_input"
                          />
                        </div>
                      </div>

                      <div className="mince_form_row">
                        <div className="mince_form_group">
                          <label htmlFor="zipCode" className="mince_form_label">
                            Zip Code
                          </label>
                          <input
                            id="zipCode"
                            name="zipCode"
                            type="text"
                            placeholder="10001"
                            value={address.zipCode}
                            onChange={handleAddressChange}
                            className="mince_form_input"
                          />
                        </div>
                        <div className="mince_form_group">
                          <label htmlFor="phone" className="mince_form_label">
                            Phone
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="text"
                            placeholder="(123) 456-7890"
                            value={address.phone}
                            onChange={handleAddressChange}
                            className="mince_form_input"
                          />
                        </div>
                      </div>

                      <div className="mince_save_address_option">
                        <input
                          type="checkbox"
                          id="save-address"
                          checked={saveAddress}
                          onChange={() => setSaveAddress(!saveAddress)}
                        />
                        <label htmlFor="save-address">Save this address as default for future orders</label>
                      </div>
                    </div>

                    <div className="mince_checkout_button_container">
                      <button className="mince_checkout_button" onClick={handleCheckout}>
                        Complete Order
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

